import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import WaiterHub from "./WaiterHub";
import StationsPage from "./StationsPage";
import MenuAdminPage from "./MenuAdminPage";
import SalesPage from "./SalesPage";
import UsersPage from "./UsersPage";
import { LayoutGrid, ClipboardList, ChefHat, UtensilsCrossed, BarChart3, Users, LogOut, AlertTriangle, BellRing, ShoppingBag, CreditCard, UserCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACK_MINUTES = 5;
const CALL_WARN_MIN = 2;

const CALL_META = {
  order: { label: "Wants to order", icon: ShoppingBag, color: "text-brand-700" },
  pay: { label: "Wants to pay", icon: CreditCard, color: "text-sea" },
  waiter: { label: "Needs a waiter", icon: BellRing, color: "text-amber-600" },
};

const ALL_TABS = [
  { id: "waiter", label: "Waiter", icon: ClipboardList, roles: ["admin", "staff"], comp: WaiterHub },
  { id: "stations", label: "Kitchen & Bar", icon: ChefHat, roles: ["admin", "staff"], comp: StationsPage },
  { id: "menu", label: "Menu", icon: UtensilsCrossed, roles: ["admin"], comp: MenuAdminPage },
  { id: "sales", label: "Sales", icon: BarChart3, roles: ["admin"], comp: SalesPage },
  { id: "users", label: "Users", icon: Users, roles: ["admin"], comp: UsersPage },
];

const playAlarm = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.25].forEach((t) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination); o.type = "triangle"; o.frequency.value = 660;
      g.gain.setValueAtTime(0.001, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + t + 0.04);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.22);
      o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.24);
    });
  } catch { /* no audio */ }
};

export default function Workspace() {
  const { user, logout } = useAuth();
  const tabs = ALL_TABS.filter((t) => t.roles.includes(user.role));
  const [active, setActive] = useState(() => {
    const saved = localStorage.getItem("tribe_tab");
    return tabs.some((t) => t.id === saved) ? saved : "waiter";
  });
  const pick = (id) => { setActive(id); localStorage.setItem("tribe_tab", id); };
  const ActiveComp = (tabs.find((t) => t.id === active) || tabs[0]).comp;
  const [waiterView, setWaiterView] = useState("location");

  // Guest table calls (I want to order / Pay / Call waiter)
  const [calls, setCalls] = useState([]);
  const [callsOpen, setCallsOpen] = useState(false);
  const callsRef = useRef([]);
  callsRef.current = calls;

  // Ask once for device (browser) notification permission.
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Poll pending calls.
  useEffect(() => {
    let alive = true;
    const check = () => axios.get(`${API}/calls`).then((r) => { if (alive) setCalls(r.data); }).catch(() => {});
    check();
    const t = setInterval(check, 8000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Nag: chime + re-fire device notifications + persistent banner until every call is claimed/cleared.
  useEffect(() => {
    const nag = () => {
      const unacked = callsRef.current.filter((c) => !c.assigned_to);
      if (unacked.length === 0) { toast.dismiss("table-calls"); return; }
      playAlarm();
      if ("Notification" in window && Notification.permission === "granted") {
        unacked.forEach((c) => {
          const m = CALL_META[c.type] || CALL_META.waiter;
          try {
            new Notification(`${c.spot_label} · ${m.label}${c.pay_method ? ` (${c.pay_method})` : ""}`, {
              body: "Guest is calling — open the app and tap \u2018I\u2019ve got this\u2019",
              tag: c.id, renotify: true, requireInteraction: true,
            });
          } catch { /* notifications unavailable */ }
        });
      }
      toast.warning(`${unacked.length} table call${unacked.length > 1 ? "s" : ""} need attention`, {
        id: "table-calls", duration: Infinity,
        description: unacked.map((c) => `${c.spot_label}`).join(", "),
        action: { label: "View", onClick: () => setCallsOpen(true) },
      });
    };
    nag();
    const t = setInterval(nag, 8000);
    return () => clearInterval(t);
  }, []);

  const ackCall = async (id) => {
    try {
      await axios.post(`${API}/calls/${id}/ack`);
      setCalls((cs) => { const next = cs.filter((c) => c.id !== id); if (!next.some((c) => !c.assigned_to)) toast.dismiss("table-calls"); return next; });
    } catch { toast.error("Couldn't clear the call"); }
  };
  const claimCall = async (id) => {
    try {
      const { data } = await axios.post(`${API}/calls/${id}/claim`);
      setCalls((cs) => { const next = cs.map((c) => (c.id === id ? { ...c, assigned_to: data.assigned_to } : c)); if (!next.some((c) => !c.assigned_to)) toast.dismiss("table-calls"); return next; });
    } catch { toast.error("Couldn't claim the call"); }
  };
  const callsLate = calls.some((c) => (Date.now() - new Date(c.created_at).getTime()) / 60000 >= CALL_WARN_MIN);

  // Global "unacknowledged order" watchdog — orders still NEW after 5 min.
  const [overdue, setOverdue] = useState([]);
  const alerted = useRef(new Set());
  const readyAlerted = useRef(new Set());
  useEffect(() => {
    let alive = true;
    const check = () => axios.get(`${API}/orders`, { params: { date: format(new Date(), "yyyy-MM-dd") } })
      .then((r) => {
        if (!alive) return;
        const cutoff = Date.now() - ACK_MINUTES * 60000;
        const late = r.data.filter((o) => o.status === "new" && new Date(o.created_at).getTime() < cutoff);
        setOverdue(late);
        const fresh = late.filter((o) => !alerted.current.has(o.id));
        if (fresh.length) {
          playAlarm();
          fresh.forEach((o) => { alerted.current.add(o.id); toast.warning(`Order not acknowledged · ${o.spot_label}`, { description: `Waiting ${ACK_MINUTES}+ min — ${o.station === "kitchen" ? "Kitchen" : "Bar"}` }); });
        }
        // forget ids that are no longer late so they can re-alert if they recur
        alerted.current.forEach((id) => { if (!late.some((o) => o.id === id)) alerted.current.delete(id); });
        // Ready-to-run ping for waiters
        const ready = r.data.filter((o) => o.status === "ready" && !o.delivered);
        const freshReady = ready.filter((o) => !readyAlerted.current.has(o.id));
        if (freshReady.length) {
          playAlarm();
          freshReady.forEach((o) => { readyAlerted.current.add(o.id); toast.success(`Ready to run · ${o.spot_label}`, { description: `${o.station === "kitchen" ? "Food" : "Drinks"} ready for pickup` }); });
        }
        readyAlerted.current.forEach((id) => { if (!ready.some((o) => o.id === id)) readyAlerted.current.delete(id); });
      }).catch(() => {});
    check();
    const t = setInterval(check, 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-sand-100">
      <nav className="sticky top-0 z-40 flex items-center gap-1 border-b border-sand-300 bg-white/95 px-2 py-1.5 backdrop-blur sm:px-4" data-testid="main-tabs">
        <span className="mr-1 hidden select-none px-2 font-display text-lg tracking-wide text-brand-700 sm:block">TRIBE</span>
        <div className="flex flex-1 items-center gap-1 overflow-x-auto">
          {tabs.map((t) => {
            const Icon = t.icon;
            const on = active === t.id;
            const badge = t.id === "waiter" && overdue.length > 0;
            return (
              <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => pick(t.id)}
                className={`relative flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${on ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-sand-100"}`}>
                <Icon size={15} /> {t.label}
                {badge && <span data-testid="orders-overdue-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white ring-2 ring-white">{overdue.length}</span>}
              </button>
            );
          })}
        </div>
        <div className="flex shrink-0 items-center gap-2 pl-2">
          {calls.length > 0 && (
            <button data-testid="calls-alert" onClick={() => setCallsOpen(true)}
              title={`${calls.length} table call(s)`}
              className={`relative flex h-9 items-center gap-1.5 rounded-full px-3 text-sm font-bold animate-pulse ${callsLate ? "bg-destructive/15 text-destructive" : "bg-sea/10 text-sea"}`}>
              <BellRing size={16} /> {calls.length}
            </button>
          )}
          {overdue.length > 0 && (
            <button data-testid="overdue-alert" onClick={() => { pick("waiter"); setWaiterView("orders"); }}
              title={`${overdue.length} order(s) waiting over ${ACK_MINUTES} min`}
              className="flex h-9 items-center gap-1.5 rounded-full bg-destructive/10 px-3 text-sm font-bold text-destructive animate-pulse">
              <AlertTriangle size={16} /> {overdue.length}
            </button>
          )}
          <div className="hidden text-right sm:block">
            <p className="text-xs font-semibold leading-none text-slate-800" data-testid="current-user-name">{user.name}</p>
            <p className="text-[10px] uppercase tracking-wider text-brand-700">{user.role}</p>
          </div>
          <button data-testid="logout-btn" onClick={logout} title="Sign out"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-sand-300 text-slate-500 hover:bg-sand-100 hover:text-slate-800">
            <LogOut size={16} />
          </button>
        </div>
      </nav>
      <div className="min-h-0 flex-1">
        {active === "waiter" ? <WaiterHub view={waiterView} setView={setWaiterView} /> : <ActiveComp />}
      </div>

      <Dialog open={callsOpen} onOpenChange={setCallsOpen}>
        <DialogContent className="max-w-sm" data-testid="calls-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-xl tracking-wide">
              <BellRing size={18} className="text-sea" /> Table calls
            </DialogTitle>
            <DialogDescription className="sr-only">Guests calling for service from their spot</DialogDescription>
          </DialogHeader>
          {calls.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">No pending calls</p>
          ) : (
            <div className="space-y-2">
              {calls.map((c) => {
                const m = CALL_META[c.type] || CALL_META.waiter;
                const Icon = m.icon;
                const mins = Math.max(0, Math.floor((Date.now() - new Date(c.created_at).getTime()) / 60000));
                const late = mins >= CALL_WARN_MIN;
                return (
                  <div key={c.id} data-testid={`call-${c.id}`}
                    className="rounded-xl border border-sand-300 bg-white px-3 py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-display text-[15px] text-slate-800">{c.spot_label}</p>
                        <p className={`flex items-center gap-1 text-xs font-semibold ${m.color}`}><Icon size={13} /> {m.label}{c.pay_method ? ` · ${c.pay_method}` : ""}</p>
                      </div>
                      <span data-testid={`call-age-${c.id}`}
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${late ? "bg-destructive/15 text-destructive" : "bg-sand-100 text-slate-500"}`}>
                        {mins === 0 ? "just now" : `waiting ${mins}m`}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      {c.assigned_to ? (
                        <span data-testid={`call-handler-${c.id}`} className="flex flex-1 items-center gap-1 text-xs font-semibold text-sea">
                          <UserCheck size={13} /> {c.assigned_to === user.name ? "You've got this" : `${c.assigned_to} is on it`}
                        </span>
                      ) : (
                        <button data-testid={`call-claim-${c.id}`} onClick={() => claimCall(c.id)}
                          className="flex-1 rounded-full bg-sea/10 px-3 py-1.5 text-xs font-bold text-sea transition-colors hover:bg-sea/20">
                          I've got this
                        </button>
                      )}
                      <button data-testid={`call-ack-${c.id}`} onClick={() => ackCall(c.id)}
                        className="shrink-0 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-slate-700">
                        Done
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
